import React, { useState, useEffect } from "react";
import { X, ZoomIn } from "lucide-react";

const ERROR_IMG_SRC =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==";

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Enable full-size view on click (default: true) */
  enableZoom?: boolean;
}

export function ImageWithFallback({
  enableZoom = true,
  ...props
}: ImageWithFallbackProps) {
  const [didError, setDidError] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { src, alt, style, className, ...rest } = props;

  // Reset error state when src changes
  useEffect(() => {
    setDidError(false);
  }, [src]);

  // Close on Escape key
  useEffect(() => {
    if (!isFullScreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullScreen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isFullScreen]);

  const handleError = () => setDidError(true);

  const fullhandleClick = () => {
    if (enableZoom && !didError) {
      setIsFullScreen(true);
    }
  };

  // ── Error fallback ──
  if (didError) {
    return (
      <div
        className={`inline-block bg-gray-100 flex items-center justify-center ${className ?? ""}`}
        style={style}
      >
        <img
          src={ERROR_IMG_SRC}
          alt={alt ?? "Image failed to load"}
          {...rest}
          data-original-url={src}
        />
      </div>
    );
  }

  return (
    <>
      {/* ── Thumbnail ── */}
      <div className={`relative group ${enableZoom ? "cursor-pointer" : ""}`}>
        <img
          src={src}
          alt={alt}
          className={className}
          style={style}
          {...rest}
          onError={handleError}
          onClick={(e) => {
                e.stopPropagation();
                fullhandleClick();
              }}
        />

        {/* Zoom indicator */}
        {enableZoom && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center rounded"
          onClick={(e) => {
                e.stopPropagation();
                fullhandleClick();
              }}
          >
            <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
          </div>
        )}
      </div>

      {/* ── Full-size modal ── */}
      {isFullScreen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsFullScreen(false)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsFullScreen(false);
            }}
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image info */}
          {alt && (
            <div className="absolute top-4 left-4 text-white/70 text-sm max-w-[60%] truncate">
              {alt}
            </div>
          )}

          {/* Full-size image */}
          <img
            src={src}
            alt={alt}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={handleError}
          />
        </div>
      )}
    </>
  );
}